import React, { useState } from 'react';
import { Box, Table, TableBody, TableHead, TableRow, TableCell, FormControl, makeStyles, Theme, createStyles, Grid } from '@material-ui/core';
import { useTypedTranslation } from '@app/hooks';
import { PasslyTextField } from '@app/components';
import { PasslyTableRow } from '@app/componentsV2';
import { PasslyTableCell } from '@app/components/Table';

import { PasslyButton } from '@app/components/Buttons';
import { availableAttributes } from '@app/pages/ssoManager/addEditApplication.enums';
import { IUserCustomAttribute } from '@app/models';
import { Autocomplete } from '@material-ui/lab';
import { defaultUserClaimsMapping } from '@app/helpers';

interface IProps {
    setCustomClaimsMapping: Function;
    formValidCallback: Function;
    customClaimsMapping: IUserCustomAttribute;
    handleSaveClaim: (claim: IUserCustomAttribute) => void;
}

export const renderReadOnlyRows = (value: string, name: string) => {
    return (
        <PasslyTableRow key={name}>
            <PasslyTableCell>{value}</PasslyTableCell>
            <PasslyTableCell>
                <pre>
                    <code>{'=>'} </code>
                </pre>
            </PasslyTableCell>
            <PasslyTableCell>{name}</PasslyTableCell>
            <PasslyTableCell />
        </PasslyTableRow>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        'custom-mapping': {
            maxWidth: '100%',
            overflow: 'auto',
            marginBottom: theme.spacing(2)
        },
        buttons: {
            '& > *': {
                marginRight: theme.spacing(1)
            }
        },
        input: {
            '& .MuiInputBase-input': {
                padding: '7px 5px 6px',
            }
        },
        'attribute-arrow': {
            padding: '27px 16px 16px 38px',
            textAlign: 'center',
            '& > *': {
                minWidth: theme.spacing(3)
            }
        }
    })
);

const OpenIDConnectConfigEdit: React.SFC<IProps> = ({ setCustomClaimsMapping, customClaimsMapping, formValidCallback, handleSaveClaim }: IProps) => {
    const { translation } = useTypedTranslation();

    const classes = useStyles();

    const newClaimInitialState = {
        newClaimValue: '',
        newClaimName: ''
    };

    const [editKey, setEditKey] = useState<string>('');
    const [newClaim, setNewClaim] = useState(newClaimInitialState);
    const onEdit = (value: string, name: string) => {
        setEditKey(name);

        setNewClaim({
            newClaimValue: value,
            newClaimName: name
        });
    };

    const onDelete = (name: string) => {
        const currentCustomClaims = { ...customClaimsMapping };
        const claim = {[name]: ''};

        delete currentCustomClaims[name];
        setCustomClaimsMapping(currentCustomClaims);
        formValidCallback(true);
        handleSaveClaim(claim);
    };

    const renderTableRows = (value: string, name: string) => {
        return (
            <PasslyTableRow key={name}>
                <PasslyTableCell>{value}</PasslyTableCell>
                <PasslyTableCell>
                    <pre>
                        <code>{'=>'}</code>
                    </pre>
                </PasslyTableCell>
                <PasslyTableCell>{name}</PasslyTableCell>
                <PasslyTableCell>
                    <PasslyTableCell>
                        <PasslyButton variant='contained' color='primary' onClick={() => onEdit(value, name)}>
                            {translation('EDIT')}
                        </PasslyButton>
                    </PasslyTableCell>
                    <PasslyTableCell>
                        <PasslyButton variant='contained' color='primary' onClick={() => onDelete(name)}>
                            {translation('REMOVE')}
                        </PasslyButton>
                    </PasslyTableCell>
                </PasslyTableCell>
            </PasslyTableRow>
        );
    };

    const cancelAddClaimMap = () => {
        setEditKey('');
        setNewClaim(newClaimInitialState);
    };

    const addUpdateClaimMap = () => {
        if (!newClaim) {
            return;
        }
        const claimsMapping = { ...customClaimsMapping };
        const claim: IUserCustomAttribute = { [newClaim.newClaimName]: newClaim.newClaimValue };

        claimsMapping[newClaim.newClaimName] = newClaim.newClaimValue;

        setCustomClaimsMapping(claimsMapping);
        formValidCallback(true);
        handleSaveClaim(claim);
        cancelAddClaimMap();
    };

    const handleNewClaimChange = (name: string, value: string | null) => {
        value = value === null ? '' : value;
        setNewClaim({ ...newClaim, [name]: value });
    };

    return (
        <div className='rowcontainer'>
            <div className='configpanel-left configpanel-left--windows-logon'>
                <div className='container'>
                    <div className={classes['custom-mapping']}>
                        {(customClaimsMapping === undefined || Object.keys(customClaimsMapping)?.length === 0) && (
                            <div className='empty-map-warning warning'>
                                <div className='label warning-text'>
                                    <b>{translation('OIDC_NO_CUSTOM_CLAIMS_ADDED')}</b>
                                </div>
                            </div>
                        )}
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{translation('OIDC_CLAIM_VALUE')}</TableCell>
                                    <TableCell></TableCell>
                                    <TableCell>{translation('OIDC_CLAIM_NAME')}</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <>{Object.keys(defaultUserClaimsMapping).map((name) => renderReadOnlyRows(defaultUserClaimsMapping[name], name))}</>
                                <>
                                    {Object.keys(customClaimsMapping).length > 0 &&
                                    Object.keys(customClaimsMapping).map((name) => renderTableRows(customClaimsMapping[name], name))}
                                </>
                            </TableBody>
                        </Table>
                    </div>

                    <>
                        <Grid container direction='row' justifyContent='center'>
                            {/* ADDING ATTRIBUTE */}
                            <Grid item md={4}>
                                <Autocomplete
                                    id='newClaimValue'
                                    freeSolo
                                    fullWidth
                                    autoSelect
                                    options={availableAttributes.map((attribute) => attribute)}
                                    value={newClaim.newClaimValue}
                                    renderInput={(params) => (
                                        <PasslyTextField
                                            {...params}
                                            fullWidth
                                            label={translation('OIDC_CLAIM_VALUE')}
                                            helperText={translation('OIDC_CLAIM_VALUE_INPUT_HELPER')}
                                            onChange={(event: any) => handleNewClaimChange('newClaimValue', event.target.value)}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item md={1}>
                                <FormControl className={classes['attribute-arrow']}>
                                    <code>{'=>'}</code>
                                </FormControl>
                            </Grid>
                            <Grid item md={7}>
                                <PasslyTextField
                                    id='newClaimName'
                                    fullWidth
                                    value={newClaim.newClaimName}
                                    className={classes.input}
                                    onChange={(event: any) => handleNewClaimChange('newClaimName', event.target.value)}
                                    label={translation('OIDC_CLAIM_NAME')}
                                    helperText={translation('OIDC_CLAIM_NAME_INPUT_HELPER')}
                                />
                            </Grid>
                        </Grid>
                        <Box className={classes.buttons} display='flex' justifyContent='flex-end'>
                            <PasslyButton variant='contained' color='primary' onClick={cancelAddClaimMap}>
                                {editKey ? translation('CANCEL') : translation('CLEAR')}
                            </PasslyButton>
                            <PasslyButton
                                variant='contained'
                                color='primary'
                                disabled={newClaim.newClaimValue === '' || newClaim.newClaimName === ''}
                                onClick={addUpdateClaimMap}
                            >
                                {editKey ? translation('OIDC_UPDATE_CLAIM') : translation('OIDC_ADD_CLAIM')}
                            </PasslyButton>
                        </Box>
                    </>
                </div>
            </div>
            <div className='configpanel-right configpanel-right--windows-logon'>
                <div dangerouslySetInnerHTML={{ __html: translation('OIDC_CONFIG_HELPER_HEADER') }} />
            </div>
        </div>
    );
};

export default OpenIDConnectConfigEdit;
